import { lazy, Suspense, useEffect } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Header from 'components/Header/Header';
import Footer from 'components/Footer/Footer';
import NotFoundMessage from 'components/NotFoundMessage/NotFoundMessage';
import ScrollToTop from 'components/ScrollToTop/ScrollToTop';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import analytics from 'lib/client/analytics/analytics';
import { convertRelativeUrlToPlain, shouldRedirectToPlainPage } from 'context/PlainPage/plainPage';
import FormContainer from 'components/ContentReporting/FormContainer/FormContainer';
import { useMarketInfo } from 'context/MarketInfo/MarketInfoProvider';
import { useMastHead } from 'hooks/useMastHead';
import { isSpotifyUri } from 'components/ContentReporting/validation-utils';
import useQueryParams from 'hooks/useQueryParams';
import * as Sentry from 'lib/client/sentry/sentry';

import styles from './App.module.scss';

// Containers are chunked into separate JS files to make initial page load quicker
const ContentLegalReporting = lazy(
  () =>
    import(
      /* webpackChunkName: "ContentLegalReporting" */ 'containers/ContentReporting/ContentLegalReporting/ContentLegalReporting'
    ),
);

const ContentPolicyReporting = lazy(
  () =>
    import(
      /* webpackChunkName: "ContentPolicyReporting" */ 'containers/ContentReporting/ContentPolicyReporting/ContentPolicyReporting'
    ),
);

const SubmitFormSuccessfully = lazy(
  () =>
    import(/* webpackChunkName: "SubmitFormSuccessfully" */ 'containers/SubmitFormSuccessfully/SubmitFormSuccessfully'),
);

const App = () => {
  const { market } = useMarketInfo();
  const location = useLocation();
  const history = useHistory();
  const { header, footer } = useMastHead(market);
  const { params } = useQueryParams();

  const uriSearchParam = params.get('uri');
  const isValidUri = isSpotifyUri(uriSearchParam);

  if (!!uriSearchParam && !isValidUri) {
    Sentry.captureException(
      new Error(
        `Invalid URI (${uriSearchParam}) within the search URI param, this causes issues on pre-filling the URI field`,
      ),
    );
  }

  useEffect(() => {
    analytics.initialize(market);
  }, [market]);

  if (shouldRedirectToPlainPage(document.referrer, location.pathname)) {
    history.replace({
      ...location,
      pathname: convertRelativeUrlToPlain(location.pathname),
    });

    return null;
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <ScrollToTop />
      <>
        <Header headerData={header} />
        <main className={styles.mainContainer}>
          <FormContainer>
            <Suspense fallback={<LoadingSpinner />}>
              <Switch>
                <Route path="*/thank-you" component={SubmitFormSuccessfully} />
                <Route path="/content-policy-reporting" component={ContentPolicyReporting} />
                <Route path="/report-content" component={ContentLegalReporting} />
                <Route component={NotFoundMessage} />
              </Switch>
            </Suspense>
          </FormContainer>
        </main>
        <Footer footerData={footer} />
      </>
    </>
  );
};

export default App;
